import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { getDeviceId } from "@/lib/device";
import { toast } from "sonner";

interface RewardState {
  status: "loading" | "pending" | "completed" | "blocked";
  amountCents: number;
}

interface ClaimResponse {
  granted?: boolean;
  amount_cents?: number;
  reason?: string;
  error?: string;
}

/**
 * Watches the user's wallet reward_status and auto-claims the $5 signup
 * bonus the moment they have at least one completed send.
 *
 * Anti-abuse (device + IP) is enforced server-side in the
 * `claim-reward` edge function (which has access to the real client IP).
 */
export const useSignupReward = () => {
  const { user } = useAuth();
  const [state, setState] = useState<RewardState>({ status: "loading", amountCents: 0 });
  const [claiming, setClaiming] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from("wallets")
      .select("reward_status")
      .eq("user_id", user.id)
      .maybeSingle();
    const status = (data as any)?.reward_status as RewardState["status"] | undefined;
    setState({ status: status ?? "completed", amountCents: 500 });
  }, [user]);

  const tryClaim = useCallback(async () => {
    if (!user || claiming) return;
    setClaiming(true);
    try {
      const { data, error } = await supabase.functions.invoke<ClaimResponse>("claim-reward", {
        body: { device_id: getDeviceId() },
      });
      if (error) {
        return;
      }
      if (data?.granted) {
        toast.success("🌴 $5 unlocked! Welcome to Cash Isle.", { duration: 5000 });
        await refresh();
      } else if (data?.reason === "device_reused" || data?.reason === "ip_limit") {
        // Silently mark blocked — the next refresh will reflect it
        await refresh();
      }
    } finally {
      setClaiming(false);
    }
  }, [user, claiming, refresh]);

  // Initial load
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Auto-claim after any send completes
  useEffect(() => {
    if (state.status === "pending") {
      void tryClaim();
    }
  }, [state.status, tryClaim]);

  return { ...state, refresh, tryClaim };
};
